import { useAuth } from 'context/useAuth';
import { where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useAppSettings } from 'context/appsettingsContext';
import { useCollection } from '../../functions/hooks/useCollection';

export const notificationTypes = {
  adminLock:      'Admin Lock',
  adminUnlock:    'Admin Unlock',
  autoLock:       'Auto Lock',
  batteryTamper:  'Battery Tamper',
  hijackLock:     'Hijack Lock',
  hijackUnlock:   'Hijack Unlock',
  ignitionLock:   'Ignition Lock',
  manualPanic:    'Manual Panic',
  movementTamper: 'Movement Tamper',
  normalLock:     'Normal Lock',
  normalUnlock:   'Normal Unlock',
  remotePanic:    'Remote Panic',
  wireTamper:     'Wire Tamper',
  multipleFailedIgnitions: 'Multiple Failed Ignitions',
} as const;

export type NotificationKey = keyof typeof notificationTypes;

// 3. Append the suffixes to get all valid property names:
export type NotificationProp = `${NotificationKey}${'Push' | 'Sms'}`;

// 4. Create a map type of exactly those properties:
export type NotificationMap = {
  [P in NotificationProp]: boolean;
};

export interface ClientInterface {
  email: string;
  firstName: string;
  id: string;
  idNumber: string;
  lastName: string;
  phoneCode: string;
  phoneNumber: string;
  totalNumberOfVehicles?: number;
  idNumberIsSouthAfrican?: boolean;
  createdOnDateTime: string;
  tamperActive: boolean;
  vehicleRegistrationNumbersStr?: string;
  deviceIdsStr?: string;
  remoteIdsStr?: string; 
  auraDisabled: boolean;
  threeDtPackage: string;
  notification: NotificationMap;
}

function getTimestampDate(timestamp: any): string {
  if (!timestamp) return '';
  
  // Handle both Firebase server timestamp and client timestamp
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toISOString();
  }
  
  // If it's already a Date object
  if (timestamp instanceof Date) {
    return timestamp.toISOString();
  }
  
  return '';
}

export function mapToClientInterface(user: any, vehicles: { id: string; data: any }[] = [], devices: { id: string; data: any }[] = []): ClientInterface {

  const clientVehicles = (vehicles || []).filter(v => v.data.userId === user.id);
  const clientDeviceIds = clientVehicles.map(v => v.data.deviceId).filter(di => di);
  const clientVehicleRegistrationNumbers = clientVehicles.map(v => v.data.details.registrationNumber).filter(rn => rn);
  const clientRemoteIds: any[] = []; (devices || []).filter(d => clientDeviceIds.includes(d.id)).map(d => d.data.ConfigurationStatusLog?.configuration?.RemotesConfiguration?.RemoteConfigurations || []).forEach((rcs: any[]) => clientRemoteIds.push(...rcs.map(rc => rc.RemoteSerialNumber).filter(sn => sn)));

  return {
    id: user.id,
    tamperActive: false,
    totalNumberOfVehicles: 0,
    email: user?.data?.profile?.email || '',
    notification: user?.data?.notification || {},
    idNumber: user?.data?.profile?.idNumber || '',
    lastName: user?.data?.profile?.lastName || '',
    firstName: user?.data?.profile?.firstName || '',
    phoneCode: user?.data?.profile?.phoneCode || '',
    phoneNumber: user?.data?.profile?.phoneNumber || '',
    auraDisabled: !!user?.data?.integration?.auraDisabled,
    threeDtPackage: user?.data?.integration?.threeDtPackage || '',
    createdOnDateTime: getTimestampDate(user?.data?.createdAt),
    idNumberIsSouthAfrican: !!user?.data?.profile?.idNumberIsSouthAfrican,
    vehicleRegistrationNumbersStr: clientVehicleRegistrationNumbers.join('|'),
    deviceIdsStr: clientDeviceIds.join('|'),
    remoteIdsStr: clientRemoteIds.join('|'),
  };
}

export default function useGetUsers(filters?: string, filterData: any = {}) {
  const { authorisedUser } = useAuth();
  const settings = useAppSettings();
  const usersCollection = useCollection<any>('users');
  const vehiclesCollection = useCollection<any>('vehicles');
  const devicesCollection = useCollection<any>('devices');

  const [users, setUsers] = useState<ClientInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function getUsers() {

      setLoading(true);

      try {

        let clients: ClientInterface[] = [];

        // For BY_CLIENT_ID we scope the joins to a single user — pulling the
        // entire devices + vehicles collections here previously made
        // ViewAccount take many seconds to open on large tenants.
        if (filters === 'ACCOUNT_HOLDERS_BY_CLIENT_ID') {
          const clientId = filterData.clientId;
          const userDocs = await usersCollection.queryMany([
            where('profile.roles', 'array-contains', 'Account-Holder'),
            where('__name__', '==', clientId),
          ]);
          if (cancelled) return;
          const scopedVehicles = clientId
            ? await vehiclesCollection.queryMany([where('userId', '==', clientId)])
            : [];
          if (cancelled) return;
          const scopedDeviceIds = Array.from(new Set(
            scopedVehicles.map((v: any) => v.data?.deviceId).filter(Boolean)
          ));
          let scopedDevices: { id: string; data: any }[] = [];
          // Firestore 'in' supports up to 30 values per query — batch.
          for (let i = 0; i < scopedDeviceIds.length; i += 30) {
            const chunk = scopedDeviceIds.slice(i, i + 30);
            if (chunk.length === 0) continue;
            const part = await devicesCollection.queryMany([where('__name__', 'in', chunk)]);
            if (cancelled) return;
            scopedDevices.push(...part);
          }
          clients = userDocs.map(x => mapToClientInterface(x, scopedVehicles, scopedDevices));
        } else {
          //TODO: Deal with this madness------------------------
          let devices = await devicesCollection.queryMany();
          if (cancelled) return;
          let vehicles = await vehiclesCollection.queryMany();
          if (cancelled) return;
          //----------------------------------------------------

          switch (filters) {

            case 'ALL_USERS':
              clients = (await usersCollection.queryMany()).map(x => mapToClientInterface(x, vehicles, devices));
              break;

            case 'ALL_CLIENTS':
              clients = (await usersCollection.queryMany([where("profile.roles", "array-contains", "Account-Holder")])).map(x => mapToClientInterface(x, vehicles, devices));
              break;

            default:
              const message = `Filter not handled : ${filters}`;
              console.error(message);
              alert(message);
          }
        }

        if (cancelled) return;
        setUsers(clients);

      } catch (error: any) {
        if (cancelled) return;
        //if (axios.isAxiosError(error)) {
          setError(error.message);
        //}
      } finally {
        if (!cancelled) {
          setLoading(false);
          sortUsers('');
        }
      }
    }
    getUsers();
    return () => { cancelled = true; };
  }, [authorisedUser.accessToken, filters, settings.baseURL, usersCollection, vehiclesCollection, filterData.clientId, devicesCollection]);

  function sortUsers(label: string) {
    switch (label) {
      case 'A - Z':
        setUsers((prevState) => {
          return prevState.sort((a, b) =>
            a.firstName.localeCompare(b.firstName)
          );
        });
        break;
      case 'Z - A':
        setUsers((prevState) => {
          return prevState.sort((a, b) =>
            b.firstName.localeCompare(a.firstName)
          );
        });
        break;
      case 'Active Alerts':
        // Set users such that users with tamperActive === true are first
        setUsers((prevState) => {
          return prevState.sort((a, b) => {
            if (a.tamperActive && !b.tamperActive) return -1;
            if (!a.tamperActive && b.tamperActive) return 1;
            return 0;
          });
        });
        break;
      default:
        setUsers((prevState) => {
          return prevState.sort((a, b) =>
            b.createdOnDateTime.localeCompare(a.createdOnDateTime)
          );
        });
    }
  }

  return {
    users,
    loading,
    error,
    sortUsers,
  };
}
